import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, ArrowUpDown, ShieldCheck, Plus, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "name_asc", label: "Name: A → Z" },
];

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "sealed", label: "Sealed" },
  { key: "single", label: "Singles" },
  { key: "slab", label: "Slabs" },
];

const GRADE_PALETTE = {
  PSA: "bg-red-500/20 text-red-300 border-red-500/40",
  BGS: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  CGC: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  Raw: "bg-slate-700 text-slate-200 border-slate-600",
  "": "bg-slate-700 text-slate-200 border-slate-600",
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [vaultItems, setVaultItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("price_asc");
  const [activeVault, setActiveVault] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/products").then((r) => r.data).catch(() => []),
      api.get("/vault").then((r) => r.data.filter((v) => !v.sold)).catch(() => []),
    ]).then(([prods, vault]) => {
      setProducts(prods);
      setVaultItems(vault);
    });
  }, []);

  // Normalize everything into a single display shape:
  // { kind: 'product'|'vault', id, name, price, image_url, set_name, category, meta, raw }
  const combined = useMemo(() => {
    const prodRows = products.map((p) => ({
      kind: "product",
      id: p.id,
      name: p.name,
      price: p.price,
      image_url: p.image_url,
      set_name: p.set_name,
      category: "sealed",
      raw: p,
    }));
    const vaultRows = vaultItems.map((v) => ({
      kind: "vault",
      id: v.id,
      name: v.card_name,
      price: v.price,
      image_url: v.front_image_url,
      set_name: v.set_name,
      category: v.item_type, // "slab" or "single"
      raw: v,
    }));
    return [...prodRows, ...vaultRows];
  }, [products, vaultItems]);

  const visible = useMemo(() => {
    const filtered = combined.filter((row) => {
      if (category !== "all" && row.category !== category) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        row.name.toLowerCase().includes(s) ||
        (row.set_name || "").toLowerCase().includes(s)
      );
    });
    const sorted = [...filtered];
    if (sort === "price_asc") sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === "price_desc") sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sort === "name_asc") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [combined, q, category, sort]);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">
        // Shop
      </div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
        Sealed, singles &amp; slabs.
      </h1>
      <p className="mt-4 text-slate-400 max-w-2xl">
        Everything we&apos;ve got — sealed product, hand-picked singles, and
        authenticated slabs. Add to cart and check out whenever you&apos;re ready.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="shop-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cards, sets, products…"
            className="w-full bg-slate-900 border border-slate-800 rounded-md pl-10 pr-3 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-yellow-400"
          />
        </div>
        <div className="relative">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <select
            data-testid="shop-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-md pl-10 pr-8 py-3 text-xs font-mono uppercase tracking-wider text-white focus:outline-none focus:border-yellow-400 appearance-none cursor-pointer"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-slate-900">
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`shop-filter-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider rounded-md border ${
              category === c.key
                ? "bg-yellow-400 text-slate-950 border-yellow-400"
                : "bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {visible.map((row) =>
          row.kind === "product" ? (
            <ProductCard key={row.id} product={row.raw} />
          ) : (
            <VaultCard key={row.id} item={row.raw} onOpen={() => setActiveVault(row.raw)} />
          )
        )}
      </div>
      {visible.length === 0 && (
        <div className="text-center py-20 text-slate-500" data-testid="shop-empty">
          Nothing matches your search.
        </div>
      )}

      {activeVault && (
        <VaultLightbox item={activeVault} onClose={() => setActiveVault(null)} />
      )}
    </div>
  );
}

function VaultCard({ item, onOpen }) {
  const { add, has } = useCart();
  const inCart = has(item.id);
  const company = item.grading_company || (item.grade ? "Raw" : "");
  const palette = GRADE_PALETTE[company] || GRADE_PALETTE[""];

  const handleAdd = (e) => {
    e.stopPropagation();
    const label = `${item.card_name}${item.grading_company ? ` (${item.grading_company} ${item.grade})` : ""}`;
    add({
      id: item.id,
      source: "vault",
      name: label,
      price: item.price,
      image_url: item.front_image_url || "",
    });
    toast.success(`${label} added to cart`);
  };

  return (
    <div
      data-testid={`vault-card-${item.id}`}
      className="flex flex-col bg-slate-900 border border-slate-800 rounded-xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/60 hover:shadow-[0_8px_30px_rgba(250,204,21,0.08)]"
    >
      <button
        type="button"
        onClick={onOpen}
        className="relative aspect-[3/4] bg-slate-950 overflow-hidden text-left"
      >
        {item.front_image_url ? (
          <img
            src={item.front_image_url}
            alt={item.card_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-700">
            <ShieldCheck className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-2">
          {company && (
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border ${palette}`}>
              {company} {item.grade}
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-yellow-400 text-slate-950 font-bold">
            {item.item_type}
          </span>
        </div>
      </button>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="text-[10px] text-slate-500 font-mono uppercase tracking-[0.2em] truncate">
          {item.set_name || "TCG"}
        </div>
        <h3 className="text-white font-bold leading-snug line-clamp-2">
          {item.card_name}
        </h3>
        {item.cert_number && (
          <div className="text-[10px] text-slate-500 font-mono truncate">
            Cert {item.cert_number}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 gap-2">
          <span className="font-mono text-xl font-bold text-yellow-400">
            ${item.price.toFixed(2)}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            data-testid={`vault-cta-${item.id}`}
            className={`inline-flex items-center gap-1 font-bold px-3 py-2 rounded-md text-xs uppercase tracking-wide transition-colors ${
              inCart
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            {inCart ? (
              <>
                <Check className="w-3 h-3" /> In cart
              </>
            ) : (
              <>
                <Plus className="w-3 h-3" /> Add to cart
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function VaultLightbox({ item, onClose }) {
  const { add, has } = useCart();
  const inCart = has(item.id);
  const allImages = [
    ...(item.front_image_url ? [{ src: item.front_image_url, label: "Front" }] : []),
    ...(item.back_image_url ? [{ src: item.back_image_url, label: "Back" }] : []),
    ...(item.extra_images || []).map((src, i) => ({ src, label: `Detail ${i + 1}` })),
  ];
  const [idx, setIdx] = useState(0);
  const company = item.grading_company || (item.grade ? "Raw" : "");
  const palette = GRADE_PALETTE[company] || GRADE_PALETTE[""];

  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % allImages.length); };
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + allImages.length) % allImages.length); };

  const handleAdd = () => {
    const label = `${item.card_name}${item.grading_company ? ` (${item.grading_company} ${item.grade})` : ""}`;
    add({
      id: item.id,
      source: "vault",
      name: label,
      price: item.price,
      image_url: item.front_image_url || "",
    });
    toast.success(`${label} added to cart`);
  };

  return (
    <div
      data-testid="vault-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 overflow-y-auto"
    >
      <button
        type="button"
        data-testid="vault-lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-slate-900 border border-slate-800 rounded-md text-slate-300 hover:text-white z-10"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden max-w-5xl w-full grid grid-cols-1 md:grid-cols-2 my-auto"
      >
        <div className="relative bg-slate-950 aspect-[3/4] md:aspect-auto md:min-h-[600px] flex items-center justify-center">
          {allImages.length > 0 ? (
            <img
              src={allImages[idx].src}
              alt={allImages[idx].label}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="text-slate-600 text-sm">No images uploaded</div>
          )}
          {allImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-slate-900/80 backdrop-blur rounded-full text-white hover:bg-slate-800"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-slate-900/80 backdrop-blur rounded-full text-white hover:bg-slate-800"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-wider text-slate-300 bg-slate-950/80 backdrop-blur border border-slate-800 px-2 py-1 rounded">
                {allImages[idx].label} · {idx + 1} / {allImages.length}
              </div>
            </>
          )}
        </div>

        <div className="p-8 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {company && (
              <span className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded border ${palette}`}>
                {company} {item.grade}
              </span>
            )}
            <span className="text-xs font-mono uppercase tracking-wider px-2 py-1 rounded bg-yellow-400 text-slate-950 font-bold">
              {item.item_type}
            </span>
          </div>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-black text-white">
            {item.card_name}
          </h2>
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">
            {item.set_name}
          </div>
          {item.cert_number && (
            <div className="text-sm text-slate-300">
              Cert:{" "}
              <span className="text-yellow-400 font-mono font-bold">
                {item.cert_number}
              </span>
            </div>
          )}
          <div className="font-mono text-4xl font-black text-yellow-400 mt-3">
            ${item.price.toFixed(2)}
          </div>
          {item.description && (
            <p className="text-sm text-slate-400 leading-relaxed mt-2">
              {item.description}
            </p>
          )}

          <div className="mt-6">
            <button
              type="button"
              onClick={handleAdd}
              data-testid="vault-lightbox-add"
              className={`inline-flex items-center gap-2 font-bold px-5 py-3 rounded-md text-sm uppercase tracking-wide transition-colors ${
                inCart
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {inCart ? (
                <>
                  <Check className="w-4 h-4" /> In cart
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Add to cart
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
